import { StatusBar } from "expo-status-bar";
import { useWindowDimensions, Pressable, Platform, StyleSheet, ScrollView, Text, useColorScheme } from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import React, { useEffect, useState, useCallback } from "react";
import { View } from "../components/Themed";
import { DatePickerModal } from 'react-native-paper-dates';
import { useQuery, gql, useMutation } from "@apollo/client";
import { useNavigation } from '@react-navigation/native';
import Config from "../constants/Config";
import * as ImagePicker from 'expo-image-picker';

const INVOICES_QUERY = gql`
  query getInvoices($token: String!, $dateFrom: String, $dateTo: String) {
    invoices(token: $token, dateFrom: $dateFrom, dateTo: $dateTo) 
  }
`;

const UPLOAD_INVOICE_MUTATION = gql`
  mutation uploadInvoice($token: String!, $files: [Upload]!, $date: String!) {
    uploadInvoice(token: $token, files: $files, date: $date) {
      id
      file
    }
  }
`;

const DELETE_INVOICE_MUTATION = gql`
  mutation deleteInvoice($token: String!, $id: ID!) {
    deleteInvoice(token: $token, id: $id)
  }
`;

export default function InvoicesScreen() {
  const [token, setToken] = useState("");
  const [dateFrom, setDateFrom] = useState(new Date().toISOString().split('T')[0]);
  const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0]);
  const [openFrom, setOpenFrom] = useState(false);
  const [openTo, setOpenTo] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMonth, setUploadMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM format
  const [openMonthPicker, setOpenMonthPicker] = useState(false);
  const colorScheme = useColorScheme();
  const navigation = useNavigation();

  const { loading, error, data, refetch } = useQuery(INVOICES_QUERY, {
    variables: { token, dateFrom, dateTo },
  });
  console.log(data)

  useEffect(() => {
    async function fetchToken() {
      try {
        const storedToken = await AsyncStorage.getItem("token");
        setToken(storedToken);
      } catch (ex) {}
    }
    fetchToken();
  }, []);

  const [uploadInvoice] = useMutation(UPLOAD_INVOICE_MUTATION);
  const [deleteInvoice] = useMutation(DELETE_INVOICE_MUTATION);

  const pickDocument = async () => {
    try {
      // Request permissions
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== 'granted') {
        alert('Permission to access media library is required!');
        return;
      }

      // Launch document picker
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.All,
        allowsEditing: false,
        quality: 1,
        allowsMultipleSelection: true,
        base64: true, // We'll need base64 for upload
      });

      if (!result.canceled) {
        setSelectedFile(result.assets);
      }
    } catch (error) {
      console.error('Error picking document:', error);
      alert('Error selecting file');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      alert('Please select a file first');
      return;
    }

    setUploading(true);
    try {
      const result = await uploadInvoice({
        variables: {
          token,
          files: selectedFile.map(selectedFile => ({
            uri: selectedFile.uri,
            type: selectedFile.type || 'application/pdf',
            name: selectedFile.fileName || `invoice_${Date.now()}.pdf`,
          })),
          date: uploadMonth, // Convert YYYY-MM to YYYY-MM-DD format
        },
      });

      if (result.data) {
        setSelectedFile(null);
        refetch();
        alert('Invoice uploaded successfully!');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Error uploading invoice');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await deleteInvoice({
      variables: {
        token,
        id,
      },
    });
    if (result.data && result.data.deleteInvoice) {
      refetch();
    }
  };

  return (
    <ScrollView>
      <View style={styles.container}>

        <View style={styles.uploadSection}>
          <Text style={styles.sectionTitle}>Upload Invoice</Text>
          <Pressable style={styles.button} onPress={() => setOpenMonthPicker(true)}>
            <Text>Upload Month: {uploadMonth}</Text>
          </Pressable>
          <DatePickerModal
            locale="en"
            mode="single"
            
            visible={openMonthPicker}
            onDismiss={() => setOpenMonthPicker(false)}
            date={new Date(uploadMonth + '-01')}
            onConfirm={(params) => {
              const selectedDate = params.date;
              const year = selectedDate.getFullYear();
              const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
              setUploadMonth(`${year}-${month}`);
              setOpenMonthPicker(false);
            }}
          />
          <Pressable style={styles.button} onPress={pickDocument}>
            <Text>Select Invoice File</Text>
          </Pressable>
          
          {selectedFile && (
            <View style={styles.selectedFile}>
              <Text>Selected {selectedFile.length} file/s</Text>
              <Pressable 
                style={[styles.button, styles.uploadButton]} 
                onPress={handleUpload}
                disabled={uploading}
              >
                <Text>{uploading ? 'Uploading...' : 'Upload Invoices'}</Text>
              </Pressable>
            </View>
          )}
        </View>
        <Text style={styles.title}>Invoices</Text>
        <View style={styles.datePicker}>
          <Pressable onPress={() => setOpenFrom(true)}>
            <Text>Month: {dateFrom}</Text>
          </Pressable>
        </View>
        <DatePickerModal
          locale="en"
          mode="single"
          visible={openFrom}
          onDismiss={() => setOpenFrom(false)}
          date={new Date(dateFrom)}
          onConfirm={(params) => {
            setDateFrom(params.date.toISOString().split('T')[0]);
            setOpenFrom(false);
          }}
        />
        
        {loading && <Text>Loading...</Text>}
        {error && <Text>Error: {error.message}</Text>}
        {data && data.invoices && data.invoices.map((invoice) => (
          <View key={invoice} style={styles.invoiceItem}>
            <Pressable 
              style={styles.invoicePressable}
              onPress={() => navigation.navigate('InvoicePreview', { 
                invoiceFile: invoice, 
                dateFrom: dateFrom 
              })}
            >
              <Text>Invoice {invoice}</Text>
            </Pressable>
            <Pressable onPress={() => handleDelete(invoice)} style={styles.deleteButton}>
              <Text style={styles.deleteText}>Delete</Text>
            </Pressable>
          </View>
        ))}
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 20,
  },
  datePicker: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  button: {
    backgroundColor: '#007AFF',
    padding: 10,
    borderRadius: 5,
    marginBottom: 20,
  },
  selectedFile: {
    backgroundColor: '#f0f0f0',
    padding: 15,
    borderRadius: 5,
    marginBottom: 20,
  },
  uploadButton: {
    backgroundColor: '#28a745',
    marginTop: 10,
  },
  invoiceItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#ccc',
    marginBottom: 10,
  },
  invoicePressable: {
    flex: 1,
    padding: 10,
  },
  deleteButton: {
    backgroundColor: '#dc3545',
    padding: 8,
    borderRadius: 5,
  },
  deleteText: {
    color: 'white',
    fontWeight: 'bold',
  },
  uploadSection: {
    marginTop: 30,
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 10,
  },
});
